import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { PORTAL_FIX_REQUESTS_SEEN_KEY } from '@/components/portal/portalConstants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Eye, ExternalLink, ImageOff } from 'lucide-react';
import { format } from 'date-fns';
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type FixRequestStatus = Database['public']['Enums']['fix_request_status'];

interface FixRequest {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  description: string;
  photo_url: string | null;
  status: FixRequestStatus;
  company_notes: string | null;
  created_at: string;
  updated_at: string;
  memorial_id: string;
  memorials?: {
    first_name: string;
    last_name: string | null;
  };
}

const statusColors: Record<FixRequestStatus, string> = {
  pending: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  in_progress: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-600 border-green-500/30',
  declined: 'bg-red-500/20 text-red-600 border-red-500/30',
};

const statusLabels: Record<FixRequestStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  declined: 'Declined',
};

// Component to handle image loading with fallback for broken images
interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  fallbackMessage?: string;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ 
  src, 
  alt, 
  className, 
  fallbackMessage = "Image could not be loaded" 
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
    console.error(`Failed to load image: ${src}`);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  if (hasError) {
    return (
      <div className={`${className} bg-muted flex flex-col items-center justify-center p-4 text-center`}>
        <ImageOff className="w-8 h-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{fallbackMessage}</p>
        <p className="text-xs text-muted-foreground mt-1">URL: {src}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className={`${className} bg-muted flex items-center justify-center`}>
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
    </div>
  );
};

const PortalFixRequests = () => {
  const { company } = usePortalAuth();
  const { isReadOnly } = useReadOnly();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<FixRequestStatus | 'all'>('all');
  const [selectedRequest, setSelectedRequest] = useState<FixRequest | null>(null);
  const [notes, setNotes] = useState('');

  const { data: fixRequests, isLoading } = useQuery({
    queryKey: ['portal-fix-requests', company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      
      const { data, error } = await supabase
        .from('fix_requests')
        .select(`
          *,
          memorials:memorial_id (
            first_name,
            last_name
          )
        `)
        .eq('monument_company_id', company.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as FixRequest[];
    },
    enabled: !!company?.id,
  });

  // Mark service requests as "seen" so the sidebar badge clears after they open and leave this tab
  const fixRequestsLength = fixRequests?.length;
  useEffect(() => {
    if (company?.id && fixRequestsLength != null) {
      try {
        localStorage.setItem(PORTAL_FIX_REQUESTS_SEEN_KEY(company.id), String(fixRequestsLength));
      } catch {
        // ignore localStorage errors
      }
    }
  }, [company?.id, fixRequestsLength]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, company_notes }: { id: string; status: FixRequestStatus; company_notes?: string }) => {
      const { error } = await supabase
        .from('fix_requests')
        .update({ status, company_notes, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-fix-requests'] });
      toast.success('Request updated successfully');
    },
    onError: () => {
      toast.error('Failed to update request');
    },
  });

  const handleStatusChange = (id: string, status: FixRequestStatus) => {
    updateStatusMutation.mutate({ id, status });
  };

  const handleSaveNotes = () => {
    if (!selectedRequest) return;
    updateStatusMutation.mutate({
      id: selectedRequest.id,
      status: selectedRequest.status,
      company_notes: notes,
    });
    setSelectedRequest(null);
  };

  const openDetails = (request: FixRequest) => {
    setSelectedRequest(request);
    setNotes(request.company_notes || '');
  };

  const filteredRequests = fixRequests?.filter(
    (req) => statusFilter === 'all' || req.status === statusFilter
  ) || [];

  const pendingCount = fixRequests?.filter((r) => r.status === 'pending').length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Service Requests</h1>
          <p className="text-muted-foreground">
            Manage headstone fix requests from customers
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-yellow-500/20 text-yellow-600">
                {pendingCount} pending
              </Badge>
            )}
          </p>
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FixRequestStatus | 'all')}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No service requests found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Memorial</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="font-medium">
                        {buildMemorialDisplayName(request.memorials)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{request.customer_name}</div>
                        <div className="text-muted-foreground">{request.customer_email}</div>
                        {request.customer_phone && (
                          <div className="text-muted-foreground">{request.customer_phone}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isReadOnly ? (
                        <Badge className={statusColors[request.status]} variant="outline">
                          {statusLabels[request.status]}
                        </Badge>
                      ) : (
                        <Select
                          value={request.status}
                          onValueChange={(v) => handleStatusChange(request.id, v as FixRequestStatus)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <Badge className={statusColors[request.status]} variant="outline">
                              {statusLabels[request.status]}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(request.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDetails(request)}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Details Dialog - constrained to viewport with scrollable body */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-6 gap-0">
          <DialogHeader className="shrink-0 pr-8 pb-4 border-b border-border">
            <DialogTitle>Service Request Details</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto py-4">
                <div className="space-y-6 pr-2">
                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Customer</label>
                      <p className="text-foreground">{selectedRequest.customer_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Email</label>
                      <p className="text-foreground break-all">{selectedRequest.customer_email}</p>
                    </div>
                    {selectedRequest.customer_phone && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Phone</label>
                        <p className="text-foreground">{selectedRequest.customer_phone}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Memorial</label>
                      <p className="text-foreground">
                        {buildMemorialDisplayName(selectedRequest.memorials)}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Issue Description</label>
                    <p className="text-foreground mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap break-words">
                      {selectedRequest.description}
                    </p>
                  </div>

                  {/* Photo - full image visible (no crop), click to open larger */}
                  {selectedRequest.photo_url && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Photo</label>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-2">Click image to view full size</p>
                      <a
                        href={selectedRequest.photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full max-w-xs aspect-square rounded-lg border border-border overflow-hidden bg-muted hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        <ImageWithFallback
                          src={selectedRequest.photo_url}
                          alt="Issue photo - click to view full size"
                          className="max-w-full max-h-full w-auto h-auto object-contain"
                          fallbackMessage="Image could not be loaded. This may indicate a storage configuration issue."
                        />
                      </a>
                    </div>
                  )}

                  {/* Status */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    {isReadOnly ? (
                      <div className="mt-1">
                        <Badge className={statusColors[selectedRequest.status]} variant="outline">
                          {statusLabels[selectedRequest.status]}
                        </Badge>
                      </div>
                    ) : (
                      <Select
                        value={selectedRequest.status}
                        onValueChange={(v) => {
                          setSelectedRequest({ ...selectedRequest, status: v as FixRequestStatus });
                        }}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Internal Notes */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Internal Notes</label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add internal notes about this request..."
                      className="mt-1"
                      rows={3}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              {/* Actions - always visible at bottom */}
              <div className="shrink-0 flex justify-end gap-2 pt-4 border-t border-border mt-4">
                {isReadOnly ? (
                  <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                    Close
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveNotes} disabled={updateStatusMutation.isPending}>
                      {updateStatusMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalFixRequests;
